import { LandingCtaLink } from '@/landing/landing-cta-link'
import { Check } from 'lucide-react'
import { AnimatedTitle } from '@/components/animated-title'
import { LandingHeroVisual } from '@/components/landing/landing-hero-visual'
import { MarketingPageShell } from '@/components/landing/marketing-page-shell'
import { useGuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { MarketingSiteFooter } from '@/components/landing/marketing-site-footer'
import { tenantBrandTypographyClass } from '@/components/client-brand'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { MARKETING_DIVIDER_BORDER_MAX_MD } from '@/landing/landing-surfaces'
import { landingOverride } from '@/landing/registry'
import { LandingLayoutByPreset } from '@/landing/layouts/landing-layouts'
import { LandingSectionRenderer, getMarketingSectionBandProps } from '@/landing/blocks/landing-blocks'
import { MarketingSection } from '@/components/marketing/marketing-section'
import { getMarketingShellChrome } from '@/landing/marketing-shell-chrome'
import { CURRENT_CLIENT } from '@/constants'
import { isCinematicBackgroundVisual, resolveLanding, type ClientWithLanding } from '@/landing/resolve-landing'
import type { LandingHeroConfig } from '@/types/client-landing'

const sharedLogoTitleClass =
  'inline-block h-8 aspect-[123/27] bg-foreground md:h-9'

function HeroProofPoints({ points }: { points: string[] }) {
  return (
    <ul className="flex max-w-xl flex-col gap-2 text-left text-sm text-muted-foreground">
      {points.map((point) => (
        <li key={point} className="flex gap-2 leading-relaxed">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span>{point}</span>
        </li>
      ))}
    </ul>
  )
}

function HeroCtaRow({
  hero,
  centered,
  className,
}: {
  hero: LandingHeroConfig
  centered?: boolean
  className?: string
}) {
  if (!hero.primaryCta && !hero.secondaryCta) return null
  return (
    <div className={cn('flex flex-wrap gap-3', centered && 'justify-center', className)}>
      {hero.primaryCta ? (
        <Button asChild size="lg" pressFeedback>
          <LandingCtaLink to={hero.primaryCta.to}>{hero.primaryCta.label}</LandingCtaLink>
        </Button>
      ) : null}
      {hero.secondaryCta ? (
        <Button asChild variant="outline" size="lg" pressFeedback>
          <LandingCtaLink to={hero.secondaryCta.to}>{hero.secondaryCta.label}</LandingCtaLink>
        </Button>
      ) : null}
    </div>
  )
}

/**
 * Public marketing `/` for unauthenticated sessions only — see App.tsx route split.
 */
export function PublicLandingPage() {
  const { guestTheme, guestPreference, setGuestTheme } = useGuestSurfaceTheme()
  const client = CURRENT_CLIENT as ClientWithLanding
  const resolved = resolveLanding(client, guestTheme)
  const {
    preset,
    hero,
    sections,
    landingComponent,
    splitVisualPanel,
    splitHeroMobileFullScreen,
    splitHeroFullOpacityBackground,
    splitHeroLayout,
    chrome,
    navLinks,
    surface,
  } = resolved
  const isMarketingSurface = surface === 'marketing'
  const shellChrome = getMarketingShellChrome(client)

  const Override = landingOverride(landingComponent)
  if (Override) {
    return <Override />
  }

  const logoPath = client.logoPath
  const cinematicHero = preset === 'splitHero'
  const centered = preset === 'centered'
  const tagline = client.tagline || hero.headline || ''
  const useAsciiOrImageCenter =
    centered &&
    hero.visual &&
    (hero.visual.type === 'ascii' ||
      hero.visual.type === 'asciiAnimation' ||
      hero.visual.type === 'image')

  const splitVisual =
    splitVisualPanel ??
    (isCinematicBackgroundVisual(hero.visual) ? hero.visual : undefined)

  const heroScrollBelow =
    splitHeroMobileFullScreen ? (
      <div className="flex max-w-xl flex-col gap-8">
        {hero.subhead ? (
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">{hero.subhead}</p>
        ) : null}
        {hero.proofPoints && hero.proofPoints.length > 0 ? (
          <HeroProofPoints points={hero.proofPoints} />
        ) : null}
        <HeroCtaRow hero={hero} />
      </div>
    ) : null

  const heroMobileMinimal = splitHeroMobileFullScreen ? (
    <div className="flex max-w-xl flex-col gap-3 text-left drop-shadow-sm sm:gap-4">
      <h1
        className={cn(
          tenantBrandTypographyClass('font-bold'),
          'text-3xl tracking-tight text-foreground sm:text-4xl'
        )}
      >
        {hero.headline}
      </h1>
      {tagline ? (
        <p className="text-lg font-medium leading-snug text-foreground/90 sm:text-xl">{tagline}</p>
      ) : null}
    </div>
  ) : null

  const heroOnImageLight = cinematicHero && splitHeroLayout.heroOnImage === 'light'

  const heroDesktopOverlay = (
    <div
      className={cn(
        'flex max-w-xl flex-col gap-4 text-left sm:gap-5 md:gap-6',
        cinematicHero && 'max-md:drop-shadow-none md:drop-shadow-sm',
        cinematicHero && splitHeroLayout.copyOffsetClass
      )}
    >
      {hero.eyebrow && !cinematicHero ? (
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{hero.eyebrow}</p>
      ) : null}

      {!cinematicHero && !useAsciiOrImageCenter && logoPath ? (
        <img src={logoPath} alt="" className="h-14 w-auto object-contain dark:brightness-0 dark:invert md:h-[3.25rem]" />
      ) : null}

      {!cinematicHero && !useAsciiOrImageCenter && !logoPath ? (
        <div className="inline-flex justify-center" aria-hidden>
          <AnimatedTitle logoOnLightBackground logoClassName={sharedLogoTitleClass} />
        </div>
      ) : null}

      {!cinematicHero && useAsciiOrImageCenter ? (
        <LandingHeroVisual visual={hero.visual} logoPath={logoPath} className="w-full max-w-md" />
      ) : null}

      <h1
        className={cn(
          tenantBrandTypographyClass('font-bold'),
          'tracking-tight',
          cinematicHero && heroOnImageLight ? 'text-white' : 'text-foreground',
          cinematicHero ? splitHeroLayout.heroHeadlineClass : 'text-[length:var(--text-display)] leading-none drop-shadow-sm',
          centered && 'text-balance'
        )}
      >
        {hero.headline}
      </h1>

      {hero.subhead ? (
        <p
          className={cn(
            'max-w-xl text-lg font-semibold leading-tight text-foreground sm:text-xl',
            centered && 'mx-auto text-center lg:text-xl',
            cinematicHero && heroOnImageLight && 'font-normal text-white sm:text-lg',
            cinematicHero && !heroOnImageLight && 'font-normal text-muted-foreground sm:text-lg'
          )}
        >
          {hero.subhead}
        </p>
      ) : null}

      {hero.proofPoints && hero.proofPoints.length > 0 ? (
        <HeroProofPoints points={hero.proofPoints} />
      ) : null}

      <HeroCtaRow hero={hero} centered={centered} />
    </div>
  )

  const heroBackground =
    cinematicHero && splitVisual ? (
      <LandingHeroVisual visual={splitVisual} logoPath={logoPath} asBackground />
    ) : undefined

  const heroAriaLabel =
    cinematicHero && splitVisual && 'alt' in splitVisual && splitVisual.alt
      ? splitVisual.alt
      : undefined

  const belowFold =
    sections && sections.length > 0 ? (
      isMarketingSurface ? (
        <>
          {sections.map((section, i) => {
            const band = getMarketingSectionBandProps(section)
            return (
              <MarketingSection key={i} id={band.id} variant={band.variant ?? 'default'}>
                <LandingSectionRenderer section={section} sectionIndex={i} surface="marketing" />
              </MarketingSection>
            )
          })}
        </>
      ) : (
        <div
          className={cn(
            'flex w-full flex-col gap-12 max-md:border-t max-md:pt-10',
            MARKETING_DIVIDER_BORDER_MAX_MD
          )}
        >
          {sections.map((section, i) => (
            <LandingSectionRenderer key={i} section={section} sectionIndex={i} surface="app" />
          ))}
        </div>
      )
    ) : null

  return (
    <MarketingPageShell
      stickyOverlayHeader={isMarketingSurface ? true : shellChrome.stickyOverlayHeader}
      stickyHeaderSolid={isMarketingSurface ? true : shellChrome.stickyHeaderSolid}
      stickyHeaderContentInset={isMarketingSurface ? true : shellChrome.stickyHeaderContentInset}
      transparentShell={isMarketingSurface ? false : shellChrome.transparentShell}
      signInLabel={shellChrome.signInLabel}
      registerLabel={shellChrome.registerLabel}
      hideRegister={shellChrome.hideRegister}
      hideSignIn={shellChrome.hideSignIn}
      guestTheme={guestTheme}
      guestPreference={guestPreference}
      onGuestThemeChange={setGuestTheme}
      navLinks={isMarketingSurface ? navLinks : undefined}
      headerSurfaceVariant={isMarketingSurface ? 'cream' : undefined}
      footer={<MarketingSiteFooter />}
    >
      <LandingLayoutByPreset
        preset={preset}
        hero={heroDesktopOverlay}
        heroMobile={heroMobileMinimal}
        heroScrollBelow={heroScrollBelow}
        splitHeroMobileFullScreen={splitHeroMobileFullScreen}
        splitHeroFullOpacityBackground={splitHeroFullOpacityBackground}
        splitHeroUnderStickyNav={splitHeroLayout.stickyChrome === 'overlay'}
        splitHeroHeight={splitHeroLayout.heroHeight}
        contentMaxWidth={chrome.contentMaxWidth}
        heroBackground={heroBackground}
        heroAriaLabel={heroAriaLabel}
        belowFold={belowFold}
      />
    </MarketingPageShell>
  )
}
